import React from 'react';
import { useCurrentFrame, useVideoConfig, interpolate } from 'remotion';
import { LayerWrapper } from './LayerWrapper';
import type {
  ImageLayer as ImageLayerConfig,
  VideoLayer as VideoLayerConfig,
  UploadedAsset,
  IdleAnimation,
} from '../../types';

// ─── Idle animation helper ────────────────────────────────────

function computeIdleTransform(
  idleAnim: IdleAnimation,
  speed: number,
  frame: number,
  enterDelay: number,
  enterDuration: number,
  exitAt: number,
  durationInFrames: number
): string {
  if (idleAnim === 'none') return '';

  // Map frame to 0-1 progress over the visible window
  const start = enterDelay + enterDuration;
  const end = exitAt < 0 ? durationInFrames : exitAt;
  const p = interpolate(frame, [start, Math.max(start + 1, end)], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  // Scale and pan magnitudes driven by speed (0-1)
  const zoom = 0.08 + speed * 0.12;   // 8–20% zoom range
  const pan  = 4 + speed * 8;          // 4–12% pan range

  switch (idleAnim) {
    case 'zoom-in':
      return `scale(${interpolate(p, [0, 1], [1, 1 + zoom])})`;
    case 'zoom-out':
      return `scale(${interpolate(p, [0, 1], [1 + zoom, 1])})`;
    case 'pan-left':
      return `translateX(${interpolate(p, [0, 1], [0, -pan])}%)`;
    case 'pan-right':
      return `translateX(${interpolate(p, [0, 1], [0, pan])}%)`;
    case 'ken-burns-1': {
      const s = interpolate(p, [0, 1], [1, 1 + zoom]);
      const tx = interpolate(p, [0, 1], [0, pan / 2]);
      return `scale(${s}) translateX(${tx}%)`;
    }
    case 'ken-burns-2': {
      const s = interpolate(p, [0, 1], [1 + zoom, 1]);
      const tx = interpolate(p, [0, 1], [-(pan / 2), 0]);
      return `scale(${s}) translateX(${tx}%)`;
    }
    case 'ken-burns-3': {
      const s = interpolate(p, [0, 1], [1, 1 + zoom]);
      const tx = interpolate(p, [0, 1], [0, -(pan / 2)]);
      return `scale(${s}) translateX(${tx}%)`;
    }
    case 'ken-burns-4': {
      const s = interpolate(p, [0, 1], [1 + zoom, 1]);
      const tx = interpolate(p, [0, 1], [pan / 2, 0]);
      return `scale(${s}) translateX(${tx}%)`;
    }
    default:
      return '';
  }
}

// ─── Image overlay ────────────────────────────────────────────

interface ImageLayerProps {
  layer: ImageLayerConfig;
  asset?: UploadedAsset;
}

export function ImageLayer({ layer, asset }: ImageLayerProps) {
  const frame = useCurrentFrame();
  const { durationInFrames } = useVideoConfig();

  const idleAnim = layer.idleAnimation ?? 'none';
  const idleSpeed = Math.min(1, Math.max(0, layer.idleSpeed ?? 0.5));
  const { enterDelay, enterDuration, exitAt } = layer.animation;

  const idleTransform = computeIdleTransform(
    idleAnim, idleSpeed, frame, enterDelay, enterDuration, exitAt, durationInFrames
  );

  if (!asset) {
    return (
      <LayerWrapper position={layer.position} animation={layer.animation} opacity={layer.opacity}
        border={layer.border} borderRadius={layer.borderRadius} shadow={layer.shadow}>
        <div style={{
          width: '100%', height: '100%',
          background: '#1e293b',
          border: '2px dashed #334155',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#475569', fontSize: 14,
        }}>
          {layer.assetKey}
        </div>
      </LayerWrapper>
    );
  }

  return (
    <LayerWrapper position={layer.position} animation={layer.animation} opacity={layer.opacity}
      border={layer.border} borderRadius={layer.borderRadius} shadow={layer.shadow}>
      {/* overflow:hidden so zoom/pan stays clipped to the layer bounds */}
      <div style={{ width: '100%', height: '100%', overflow: 'hidden', borderRadius: 'inherit' }}>
        <img
          src={asset.url}
          alt=""
          style={{
            width: '100%',
            height: '100%',
            objectFit: layer.objectFit ?? 'cover',
            display: 'block',
            transform: idleTransform,
            transformOrigin: 'center center',
            willChange: idleAnim !== 'none' ? 'transform' : 'auto',
          }}
        />
      </div>
    </LayerWrapper>
  );
}

// ─── Video overlay ────────────────────────────────────────────

interface VideoLayerProps {
  layer: VideoLayerConfig;
  asset?: UploadedAsset;
}

export function VideoLayer({ layer, asset }: VideoLayerProps) {
  if (!asset) {
    return (
      <LayerWrapper position={layer.position} animation={layer.animation} opacity={layer.opacity}
        border={layer.border} borderRadius={layer.borderRadius} shadow={layer.shadow}>
        <div style={{
          width: '100%', height: '100%',
          background: '#0f172a',
          border: '2px dashed #334155',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#475569', fontSize: 14,
        }}>
          ▶ {layer.assetKey}
        </div>
      </LayerWrapper>
    );
  }

  return (
    <LayerWrapper position={layer.position} animation={layer.animation} opacity={layer.opacity}
      border={layer.border} borderRadius={layer.borderRadius} shadow={layer.shadow}>
      <video
        src={asset.url}
        style={{
          width: '100%',
          height: '100%',
          objectFit: layer.objectFit ?? 'cover',
          display: 'block',
          borderRadius: 'inherit',
        }}
        autoPlay
        loop
        muted={layer.muted ?? true}
        playsInline
      />
    </LayerWrapper>
  );
}
